import { createContext, use } from 'react';
import invariant from 'tiny-invariant';

import type { HilSignal } from '@cadstrom/backend/shared';

export interface SignalReferenceContextValue {
	/** The in-scope signals keyed by exact, case-sensitive name. */
	signalsByName: ReadonlyMap<string, HilSignal>;
	/** Every in-scope signal, for chips that resolve sibling references (e.g. tie targets). */
	allSignals: HilSignal[];
}

export const SignalReferenceContext = createContext<SignalReferenceContextValue | null>(null);

/**
 * Returns the signals that `:signal[…]` markdown references resolve against.
 *
 * @param required - Pass `false` to get `undefined` instead of throwing when no provider is
 *   mounted, so consumers can degrade gracefully outside signal-bearing surfaces.
 * @returns The context value, or `undefined` when optional and absent.
 */
export function useSignalReferenceContext(required?: true): SignalReferenceContextValue;
export function useSignalReferenceContext(required: false): SignalReferenceContextValue | undefined;
export function useSignalReferenceContext(required = true): SignalReferenceContextValue | undefined {
	const value = use(SignalReferenceContext);
	invariant(value !== null || !required, 'SignalReferenceContext is not provided');
	return value ?? undefined;
}
